// ServiceVideoSection.tsx
// Full-width service-specific video shown above "About the company" card
import { Box, Typography } from '@mui/material';
import React from 'react';

interface ServiceVideoSectionProps {
  videoUrl?: string | null;
  serviceName?: string;
}

const parseVideoEmbedUrl = (url: string): string | null => {
  try {
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?rel=0&modestbranding=1&enablejsapi=1`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return null;
  } catch { return null; }
};

const ServiceVideoSection: React.FC<ServiceVideoSectionProps> = ({ videoUrl, serviceName }) => {
  const [visible, setVisible] = React.useState(true);
  if (!videoUrl) return null;
  const embedUrl = parseVideoEmbedUrl(videoUrl);
  if (!embedUrl) return null;

  React.useEffect(() => {
    const handler = (e: MessageEvent) => {
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (data?.event === 'onError' && [100, 101, 150].includes(Number(data?.info))) {
          setVisible(false);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [embedUrl]);

  if (!visible) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        ▶ About this service
      </Typography>
      <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
        <iframe
          src={embedUrl}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={`${serviceName || 'Service'} video`}
          loading="lazy"
          onError={() => setVisible(false)}
        />
      </Box>
    </Box>
  );
};

export default React.memo(ServiceVideoSection);
